<script>
    import {createEventDispatcher} from 'svelte';

    export let files = [];
    export let error = '';

    const MAX_FILES = 5;
    const MAX_SIZE = 5 * 1024 * 1024; // 5 MB
    const ALLOWED = ['image/jpeg', 'image/png'];

    const dispatch = createEventDispatcher();

    let fileInput;
    let isDragover = false;

    function formatSize(bytes) {
        if (bytes < 1024) return `${bytes} Б`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} КБ`;
        return `${(bytes / (1024 * 1024)).toFixed(2)} МБ`;
    }

    function truncateName(name, max = 32) {
        if (name.length <= max) return name;
        const dot = name.lastIndexOf('.');
        if (dot > 0 && name.length - dot <= 8) {
            const head = name.slice(0, max - (name.length - dot) - 1);
            return `${head}…${name.slice(dot)}`;
        }
        return `${name.slice(0, max - 1)}…`;
    }

    function addFiles(newFiles) {
        if (!newFiles || !newFiles.length) return;

        const accepted = [];
        const rejected = [];

        for (const f of newFiles) {
            if (files.length + accepted.length >= MAX_FILES) {
                rejected.push({name: f.name, reason: `Не более ${MAX_FILES} файлов`});
                continue;
            }
            if (!ALLOWED.includes(f.type)) {
                rejected.push({name: f.name, reason: 'Только JPG и PNG'});
                continue;
            }
            if (f.size > MAX_SIZE) {
                rejected.push({name: f.name, reason: 'Размер больше 5 МБ'});
                continue;
            }
            accepted.push(f);
        }

        if (accepted.length) {
            files = [...files, ...accepted];
            dispatch('change', files);
        }

        if (rejected.length) {
            const msg = rejected.map(r => `${r.name}: ${r.reason}`).join('; ');
            dispatch('error', msg);
        } else if (accepted.length) {
            dispatch('error', '');
        }
    }

    function onSelect(event) {
        const list = event.target.files;
        addFiles(list);
        // allow re-selecting the same file later
        if (fileInput) fileInput.value = '';
    }

    function onDrop(event) {
        event.preventDefault();
        isDragover = false;
        const dt = event.dataTransfer;
        if (dt && dt.files) {
            addFiles(dt.files);
        }
    }

    function onDragOver(event) {
        event.preventDefault();
        isDragover = true;
    }

    function onDragEnter(event) {
        event.preventDefault();
        isDragover = true;
    }

    function onDragLeave() {
        isDragover = false;
    }

    function removeFile(index) {
        files = files.filter((_, i) => i !== index);
        dispatch('change', files);
        dispatch('error', '');
    }
</script>

<div class="dropzone-wrap">
    <label class="dropzone"
           class:is-dragover={isDragover}
           class:has-error={!!error}
           on:dragover={onDragOver}
           on:dragenter={onDragEnter}
           on:dragleave={onDragLeave}
           on:drop={onDrop}>
        <input
                type="file"
                class="visually-hidden"
                multiple
                accept="image/jpeg,image/png"
                bind:this={fileInput}
                on:change={onSelect}/>
        <svg class="dropzone__icon" viewBox="0 0 24 24" width="32" height="32" aria-hidden="true">
            <path d="M19 13v5a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-5h2v5h10v-5h2zM12 3l5 5h-3v6h-4V8H7l5-5z" fill="currentColor"/>
        </svg>
        <div class="dropzone__text">Перетащите фото или нажмите, чтобы выбрать</div>
        <div class="dropzone__sub">JPG, PNG, до 5 МБ, не более 5 файлов</div>
    </label>

    {#if files.length}
        <ul class="chips" aria-label="Прикреплённые файлы">
            {#each files as f, i (f.name + '_' + f.size + '_' + i)}
                <li class="chip">
                    <span class="chip__name" title={f.name}>{truncateName(f.name)}</span>
                    <span class="chip__size">{formatSize(f.size)}</span>
                    <button type="button"
                            class="chip__remove"
                            aria-label={`Удалить файл ${f.name}`}
                            on:click={() => removeFile(i)}>×</button>
                </li>
            {/each}
        </ul>
    {/if}
</div>

<style>
    .dropzone-wrap {
        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    .dropzone {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 6px;
        padding: 18px 12px;
        border: 1.5px dashed #c9c9c9;
        border-radius: 8px;
        background: #fafafa;
        color: #555;
        cursor: pointer;
        text-align: center;
        transition: border-color 120ms ease, background 120ms ease, color 120ms ease;
    }

    .dropzone:hover,
    .dropzone:focus-within {
        border-color: var(--main-color, #a06840);
        color: #333;
    }

    .dropzone.is-dragover {
        border-color: var(--main-color, #a06840);
        background: rgba(160, 104, 64, 0.06);
        color: var(--main-color, #a06840);
    }

    .dropzone.has-error {
        border-color: var(--red, #d0021b);
    }

    .dropzone__icon {
        color: var(--main-color, #a06840);
    }

    .dropzone__text {
        font-size: 14px;
        font-weight: 500;
    }

    .dropzone__sub {
        font-size: 12px;
        color: #888;
    }

    .visually-hidden {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border: 0;
    }

    .chips {
        list-style: none;
        margin: 0;
        padding: 0;
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
    }

    .chip {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 4px 4px 4px 10px;
        border-radius: 16px;
        background: #f1ece7;
        color: #3a3a3a;
        font-size: 12px;
        line-height: 1.2;
        max-width: 100%;
    }

    .chip__name {
        max-width: 200px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .chip__size {
        color: #888;
    }

    .chip__remove {
        border: none;
        background: transparent;
        font-size: 18px;
        line-height: 1;
        padding: 0 6px;
        cursor: pointer;
        color: #777;
        border-radius: 50%;
    }

    .chip__remove:hover,
    .chip__remove:focus-visible {
        color: var(--red, #d0021b);
        outline: none;
    }
</style>
